import React, { useMemo, useState } from 'react';
import useEmployeeSelfServiceAssignment from './useEmployeeSelfServiceAssignment';
import './service-staff-assignment.css';

const EmployeeSelfServiceAssignmentSection = ({
  employee,
  canSelfManage = false,
}) => {
  const [search, setSearch] = useState('');
  const {
    loading,
    error,
    selfServiceEnabled,
    canEdit,
    displayServices,
    assignedServices,
    savingServiceId,
    isAssignedToService,
    toggleAssignment,
  } = useEmployeeSelfServiceAssignment({ employee, canSelfManage });

  const filteredServices = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return displayServices;
    }

    return displayServices.filter((service) =>
      String(service?.name || '').toLowerCase().includes(query)
    );
  }, [displayServices, search]);

  const handleCheckboxChange = async (service, event) => {
    await toggleAssignment(service, event.target.checked);
  };

  if (loading) {
    return <div className="ssa-emptyState">Loading services...</div>;
  }

  if (!selfServiceEnabled && assignedServices.length === 0) {
    return (
      <div className="ssa-emptyState">
        You are not assigned to any services yet. Ask the owner to assign you or enable employee
        self-assignment in Manage Services.
      </div>
    );
  }

  return (
    <div className="ssa-selfSection">
      {!selfServiceEnabled ? (
        <p className="ssa-embedSub">
          Your owner has not enabled employee self-assignment. Contact them to change your service
          assignments.
        </p>
      ) : canEdit ? (
        <p className="ssa-embedSub">
          Check a service to assign yourself. Uncheck to remove yourself. Only services at your
          assigned locations are shown.
        </p>
      ) : (
        <p className="ssa-embedSub">
          Services this employee is assigned to perform.
        </p>
      )}

      {error ? <div className="ssa-warning">{error}</div> : null}

      {canEdit ? (
        <input
          className="ssa-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search services..."
          aria-label="Search services"
        />
      ) : null}

      {filteredServices.length === 0 ? (
        <div className="ssa-listEmpty">
          {canEdit
            ? 'No eligible services found at your assigned locations.'
            : 'No service assignments yet.'}
        </div>
      ) : (
        <ul className="ssa-serviceList">
          {filteredServices.map((service) => {
            const assigned = isAssignedToService(service);
            const isSaving = savingServiceId === service.id;

            return (
              <li key={service.id} className="ssa-serviceListItem">
                {canEdit ? (
                  <label className="ssa-serviceListLabel">
                    <input
                      type="checkbox"
                      checked={assigned}
                      disabled={Boolean(savingServiceId)}
                      onChange={(event) => handleCheckboxChange(service, event)}
                    />
                    <span className="ssa-serviceListText">
                      <span className="ssa-serviceListName">{service.name || 'Untitled service'}</span>
                      {service.duration ? (
                        <span className="ssa-serviceListMeta">{service.duration} min</span>
                      ) : null}
                    </span>
                    {isSaving ? <span className="ssa-serviceListSaving">Saving...</span> : null}
                  </label>
                ) : (
                  <div className="ssa-serviceListReadonly">
                    <span className="ssa-serviceListName">{service.name || 'Untitled service'}</span>
                    {service.duration ? (
                      <span className="ssa-serviceListMeta">{service.duration} min</span>
                    ) : null}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default EmployeeSelfServiceAssignmentSection;
